import { authExchange } from '@urql/exchange-auth';
import { type CacheExchangeOpts, cacheExchange } from '@urql/exchange-graphcache';
import dayjs from 'dayjs';
import { createClient, errorExchange, fetchExchange } from 'urql';
import { array, object, string } from 'yup';
import schema from '../client/introspection.json';
import { clearTokens, getTokens } from './authStore';
import { UNAUTHORIZED_QUERY } from './constants';
import { errorEventEmitter } from './errorEmitter';
import { updateFunctions } from './updateList';

let _tokens: {
  access_token?: string;
  refresh_token?: string;
  expires_at?: number;
  id_token?: string;
} | null = null;

export const urqlClient = createClient({
  url: import.meta.env.VITE_APP_BACKEND_URI + import.meta.env.VITE_APP_BACKEND_GRAPHQL_PATH || '',
  suspense: true,
  requestPolicy: 'cache-and-network',
  exchanges: [
    cacheExchange({
      // needed for Schema Awareness https://commerce.nearform.com/open-source/urql/docs/graphcache/schema-awareness/
      schema: schema as CacheExchangeOpts['schema'],
      keys: new Proxy(
        {},
        {
          get() {
            // tells urql to use the uuid instead of id for caching
            return (data: { uuid: string | undefined }) => data.uuid || null;
          },
        },
      ),
      updates: {
        Mutation: {
          // when one of the provided mutations is run, the queries with the provided fieldname will be updated
          ...updateFunctions,
          // need to overwrite behaviour, because nextcloud is rendered in licensedProducts in Organization
          createNextclouds(_result, args, cache, _info) {
            try {
              const schema = object({
                input: array(
                  object({
                    organization: object({
                      connect: object({
                        where: object({
                          node: object({ uuid: string().required() }),
                        }).required(),
                      }).required(),
                    }).required(),
                  }).required(),
                ).required(),
              }).required();
              const validatedArgs = schema.validateSync(args, {
                stripUnknown: true,
              });
              const organizationUuid = validatedArgs.input[0].organization.connect.where.node.uuid;
              cache.invalidate({
                __typename: 'Organization',
                uuid: organizationUuid,
              });
            } catch (error) {
              console.error('organizationUuid was not provided, but is needed to revalidate queries');
              console.error(error);
            }
          },
        },
      },
    }),
    authExchange(async (utils) => {
      _tokens = await getTokens();

      return {
        addAuthToOperation(operation) {
          if (_tokens) {
            return utils.appendHeaders(operation, {
              Authorization: `Bearer ${_tokens.access_token}`,
            });
          }
          return operation;
        },
        willAuthError(_operation) {
          if (!_tokens) {
            return true;
          }

          return dayjs(_tokens.expires_at).isBefore(dayjs());
        },
        didAuthError(error, _operation) {
          return (
            error.response?.status === 401 || error.graphQLErrors.some((e) => e.extensions?.code === UNAUTHORIZED_QUERY)
          );
        },
        async refreshAuth() {
          _tokens = await getTokens();
          if (
            !_tokens?.access_token ||
            (_tokens?.expires_at && dayjs(_tokens?.expires_at).isSameOrBefore(dayjs().unix()))
          ) {
            await clearTokens();
            _tokens = null;
          }
        },
      };
    }),
    errorExchange({
      onError: (error) => {
        const isAuthError =
          error.graphQLErrors.some((e) => e.extensions?.code === UNAUTHORIZED_QUERY) ||
          error.networkError?.message === 'Unauthorized';
        if (isAuthError) {
          clearTokens();
          window.location.href = '/';
        } else {
          errorEventEmitter.emit(error);
        }
      },
    }),
    fetchExchange,
  ],
});
